import { CommandProps, mergeAttributes, Node } from '@tiptap/core';
import { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';
import { EditorState, TextSelection, Transaction } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { keymap } from '@tiptap/pm/keymap';

import { ReactCallout } from './component';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        callout: {
            setCallout: (type: string) => ReturnType;
        };
    }
}

export interface CalloutOptions {
    types: string[];
    defaultType: string;
}

const findNodeDepthByName = ($pos: ResolvedPos, name: string) => {
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
        if ($pos.node(depth)?.type.name === name) {
            return depth;
        }
    }

    return undefined;
};

const isEmptyCallout = (node: ProseMirrorNode) => {
    const child = node.firstChild;

    return node.childCount === 1 && Boolean(child?.isTextblock) && child?.content.size === 0;
};

export const Callout = Node.create<CalloutOptions>({
    name: 'callout',
    group: 'block',
    content: 'block+',
    defining: true,
    isolating: true,
    draggable: true,

    addOptions() {
        return {
            types: ['info', 'warning', 'tip', 'danger'],
            defaultType: 'info',
        };
    },

    addAttributes() {
        return {
            type: {
                default: this.options.defaultType,
                parseHTML: (el) => el.getAttribute('data-type'),
                renderHTML: (attrs) => ({ 'data-type': attrs.type }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(ReactCallout);
    },

    addCommands() {
        return {
            setCallout:
                (type: string) =>
                ({ chain, state }: CommandProps) => {
                    const { from, to, empty } = state.selection;
                    const selectedText = empty ? '' : state.doc.textBetween(from, to, '\n\n').trim();
                    const selectedParagraphs = selectedText
                        .split(/\n+/)
                        .map((text) => text.trim())
                        .filter(Boolean);
                    const content = selectedParagraphs.length
                        ? selectedParagraphs.map((text) => ({
                              type: 'paragraph',
                              content: [{ type: 'text', text }],
                          }))
                        : [{ type: 'paragraph' }];

                    return chain()
                        .insertContent({
                            type: this.name,
                            attrs: { type },
                            content,
                        })
                        .run();
                },
        };
    },

    // Keeps empty callout keyboard behavior consistent with regular editor blocks.
    addProseMirrorPlugins() {
        const deleteEmptyCallout = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
            const { $from, $to } = state.selection;
            const calloutDepth = findNodeDepthByName($from, this.name);

            const isCollapsed = state.selection.empty;
            const isInsideCallout = calloutDepth !== undefined && $to.depth >= calloutDepth;
            const isEmptyParagraph = $from.parent.isTextblock && $from.parent.content.size === 0;

            if (!isCollapsed || !isInsideCallout || !isEmptyParagraph || !isEmptyCallout($from.node(calloutDepth))) {
                return false;
            }

            if (dispatch) {
                const calloutStart = $from.before(calloutDepth);
                const calloutEnd = $from.after(calloutDepth);
                const paragraph = state.schema.nodes.paragraph.create();
                let tr = state.tr.replaceWith(calloutStart, calloutEnd, paragraph);
                tr = tr.setSelection(TextSelection.create(tr.doc, calloutStart + 1));
                dispatch(tr.scrollIntoView());
            }

            return true;
        };

        return [
            keymap({
                Backspace: deleteEmptyCallout,
                Delete: deleteEmptyCallout,
                Enter: (state, dispatch) => {
                    const { $from, $to } = state.selection;
                    const calloutDepth = findNodeDepthByName($from, this.name);

                    const isCollapsed = state.selection.empty;
                    const isEmptyParagraph = $from.parent.content.size === 0;
                    const isInsideCallout = calloutDepth !== undefined && $to.depth >= calloutDepth;

                    // Only trigger when cursor is in an empty paragraph inside the callout
                    if (isCollapsed && isEmptyParagraph && isInsideCallout) {
                        const afterCalloutPos = $from.after(calloutDepth);

                        if (dispatch) {
                            const paragraph = state.schema.nodes.paragraph.create();
                            let tr = state.tr.insert(afterCalloutPos, paragraph);
                            tr = tr.setSelection(TextSelection.near(tr.doc.resolve(afterCalloutPos + 1)));
                            dispatch(tr.scrollIntoView());
                        }

                        return true;
                    }

                    return false;
                },
            }),
        ];
    },
});
